import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.6px", mb: 2 }}>
    {text}
  </Typography>
);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "15px",
    "& fieldset": { borderColor: "#d1d5db" },
    "&:hover fieldset": { borderColor: "#2563eb" },
    "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "15px", "&.Mui-focused": { color: "#2563eb" } },
};

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({ click(e) { onMapClick(e.latlng); } });
  return null;
};

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2018 + 1 }, (_, index) => 2018 + index);

const initialFormData = {
  year: "",
  municipality: "",
  submunicipality: "",
  suco: "",
  location: "",
  latitude: "",
  longitude: "",
  datetime: "",
  numberofdeaths: "",
  numberofinjured: "",
};

function Addlightningdeathrecord() {
  const navigate = useNavigate();
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState([-8.7747, 125.7275]);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [pendingMarker, setPendingMarker] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [municipalities, setMunicipalities] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [sucos, setSucos] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState("");

  useEffect(() => {
    axios.post(`${backendUrl}Municipalities/municipalities_list/`)
      .then(r => setMunicipalities(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedMunicipality) {
      axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`, { adm2_code: selectedMunicipality })
        .then(r => setSubMunicipalities(r.data))
        .catch(console.error);
    } else {
      setSubMunicipalities([]);
      setSucos([]);
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      axios.post(`${backendUrl}Municipalities/suco_list/`, { adm2_code: selectedSubMunicipality })
        .then(r => setSucos(r.data))
        .catch(console.error);
    } else {
      setSucos([]);
    }
  }, [selectedSubMunicipality]);

  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
    setSelectedSubMunicipality("");
    setFormData(prev => ({ ...prev, municipality: e.target.value, submunicipality: "", suco: "" }));
  };

  const handleSubMunicipalityChange = (e) => {
    setSelectedSubMunicipality(e.target.value);
    setFormData(prev => ({ ...prev, submunicipality: e.target.value, suco: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenMap = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setMapCenter([loc.lat, loc.lng]);
          setPendingMarker(markerPosition || loc);
          setShowMap(true);
        },
        () => {
          setPendingMarker(markerPosition);
          setShowMap(true);
        }
      );
    } else {
      setPendingMarker(markerPosition);
      setShowMap(true);
    }
  };

  const handleMapConfirm = () => {
    if (pendingMarker) {
      setMarkerPosition(pendingMarker);
      setFormData(prev => ({
        ...prev,
        latitude: pendingMarker.lat.toFixed(8),
        longitude: pendingMarker.lng.toFixed(8),
      }));
    }
    setShowMap(false);
  };

  const handleMapCancel = () => {
    setPendingMarker(null);
    setShowMap(false);
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setMarkerPosition(null);
    setPendingMarker(null);
    setSelectedMunicipality("");
    setSelectedSubMunicipality("");
    setSucos([]);
    setSubmitSuccess(false);
    setSubmitError("");
  };

  const handleBackClick = () => {
    navigate("/Lightning/Reports?tab=deathdata");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess(false);
    try {
      const response = await axios.post(`${backendUrl}lightning/submit_lightning_death_record/`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError("Error occurred while submitting the form.");
      }
    } catch (error) {
      setSubmitError("Error occurred: " + error.message);
    }
  };

  return (
    <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>Add Lightning Death Record</Typography>
          <Typography sx={{ fontSize: "12px", color: "#64748b", mt: 0.25 }}>Register deaths/injuries caused by lightning</Typography>
        </Box>
        <Button onClick={handleBackClick} sx={{ textTransform: "none", fontSize: "13px", fontWeight: 600, color: "#374151" }}>
          Back to data
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>

        {submitSuccess && (
          <Alert severity="success" sx={{ fontSize: "14px", borderRadius: "8px" }}>
            Lightning death record added successfully! Form will reset in 2 seconds.
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ fontSize: "14px", borderRadius: "8px" }}>{submitError}</Alert>
        )}

        <Box>
          <SectionLabel text="Record Details" />
          <TextField label="Year" select size="small" fullWidth sx={inputSx} name="year" value={formData.year} onChange={handleChange}>
            <MenuItem value=""><em style={{ color: "#94a3b8" }}>Select a year</em></MenuItem>
            {years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
          </TextField>
        </Box>

        <Divider sx={{ borderColor: "#e2e8f0" }} />

        <Box>
          <SectionLabel text="Location Information" />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2.5 }}>
            <TextField label="Municipality" select size="small" fullWidth sx={inputSx} value={selectedMunicipality} onChange={handleMunicipalityChange}>
              <MenuItem value=""><em style={{ color: "#94a3b8" }}>Select a municipality</em></MenuItem>
              {municipalities.map((m, i) => <MenuItem key={i} value={m.adm1_code}>{m.adm1}</MenuItem>)}
            </TextField>
            <TextField label="Sub-Municipality" select size="small" fullWidth sx={inputSx} value={formData.submunicipality} onChange={handleSubMunicipalityChange} disabled={!selectedMunicipality}>
              <MenuItem value=""><em style={{ color: "#94a3b8" }}>{selectedMunicipality ? "Select a sub-municipality" : "Select a municipality first"}</em></MenuItem>
              {subMunicipalities.map((s, i) => <MenuItem key={i} value={s.adm2_code}>{s.adm2}</MenuItem>)}
            </TextField>
            <TextField label="Suco" select size="small" fullWidth sx={inputSx} value={formData.suco} onChange={handleChange} name="suco" disabled={!selectedSubMunicipality}>
              <MenuItem value=""><em style={{ color: "#94a3b8" }}>{selectedSubMunicipality ? "Select a suco" : "Select a sub-municipality first"}</em></MenuItem>
              {sucos.map((s, i) => <MenuItem key={i} value={s.adm3_code}>{s.adm3}</MenuItem>)}
            </TextField>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField label="Latitude" size="small" fullWidth sx={inputSx} value={formData.latitude} InputProps={{ readOnly: true }} placeholder="Click map to set" />
              <TextField label="Longitude" size="small" fullWidth sx={inputSx} value={formData.longitude} InputProps={{ readOnly: true }} placeholder="Click map to set" />
              <Button
                type="button"
                variant="outlined"
                onClick={handleOpenMap}
                title="Use my location & pick on map"
                sx={{
                  minWidth: "46px", height: "40px", alignSelf: "flex-start",
                  borderColor: "#d1d5db", color: "#2563eb",
                  "&:hover": { borderColor: "#2563eb", background: "#eff6ff" },
                  borderRadius: "8px", flexShrink: 0,
                }}
              >
                <MyLocationIcon sx={{ fontSize: "20px" }} />
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#e2e8f0" }} />

        <Box>
          <SectionLabel text="Case Summary" />
          <TextField
            label="Date and Time"
            type="datetime-local"
            size="small"
            fullWidth
            sx={inputSx}
            name="datetime"
            value={formData.datetime}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Number of Deaths"
              type="number"
              size="small"
              fullWidth
              sx={inputSx}
              name="numberofdeaths"
              value={formData.numberofdeaths}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Number of Injured"
              type="number"
              size="small"
              fullWidth
              sx={inputSx}
              name="numberofinjured"
              value={formData.numberofinjured}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleReset}
            sx={{ borderColor: "#d1d5db", color: "#374151", "&:hover": { borderColor: "#64748b", background: "#f8fafc" }, px: 4, py: 1, borderRadius: "8px", textTransform: "none", fontSize: "14px", fontWeight: 600 }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ background: "#2563eb", "&:hover": { background: "#1d4ed8" }, px: 4, py: 1, borderRadius: "8px", textTransform: "none", fontSize: "14px", fontWeight: 600, boxShadow: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>

      <Dialog open={showMap} onClose={handleMapCancel} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "12px", border: "1px solid #e2e8f0" } }}>
        <DialogTitle sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", py: 2 }}>
          Select Location on Map
          <Typography sx={{ fontSize: "12px", color: "#64748b", mt: 0.25, fontWeight: 400 }}>
            Click anywhere on the map to place a pin
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <MapContainer center={mapCenter} zoom={13} style={{ width: "100%", height: "450px" }}>
            <MapHomeControl />
            <MapInfoControl text="Click on the map to mark the location of the lightning death/injury." />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapClickHandler onMapClick={setPendingMarker} />
            {pendingMarker && (
              <Marker position={pendingMarker}>
                <Popup>
                  Lat: {pendingMarker.lat.toFixed(8)}<br />
                  Lng: {pendingMarker.lng.toFixed(8)}
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}>
          <Button
            onClick={handleMapCancel}
            variant="outlined"
            sx={{ borderColor: "#d1d5db", color: "#374151", "&:hover": { borderColor: "#64748b", background: "#f8fafc" }, px: 3, borderRadius: "8px", textTransform: "none", fontSize: "14px", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMapConfirm}
            variant="contained"
            disabled={!pendingMarker}
            sx={{ background: "#2563eb", "&:hover": { background: "#1d4ed8" }, "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" }, px: 3, borderRadius: "8px", textTransform: "none", fontSize: "14px", fontWeight: 600, boxShadow: "none" }}
          >
            Confirm Location
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default Addlightningdeathrecord;
